<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import CardLayout from 'dashboard/components-next/CardLayout.vue';

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const contactAttributes = useMapGetter('attributes/getContactAttributes');
const uiFlags = useMapGetter('contacts/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);

// [key, value] pairs written by Captain; the badge only shows while the
// stored value still matches, so a human overwrite drops it automatically.
const captainFilledPairs = computed(
  () =>
    props.selectedContact?.additionalAttributes?.captainFilledAttributes || []
);

const isAiFilledValue = (key, value) =>
  captainFilledPairs.value.some(
    entry =>
      Array.isArray(entry) &&
      entry[0] === key &&
      String(entry[1]) === String(value)
  );

const builtInCriteria = computed(() => {
  const { email, phoneNumber } = props.selectedContact || {};
  return [
    {
      key: 'email',
      label: t('CONTACTS_LAYOUT.DETAILS.QUALIFICATION.BUILT_IN.EMAIL'),
      value: email,
      met: Boolean(email),
      isAiFilled: false,
    },
    {
      key: 'phone',
      label: t('CONTACTS_LAYOUT.DETAILS.QUALIFICATION.BUILT_IN.PHONE'),
      value: phoneNumber,
      met: Boolean(phoneNumber),
      isAiFilled: false,
    },
  ];
});

const customCriteria = computed(() => {
  const { customAttributes = {} } = props.selectedContact || {};

  return (contactAttributes.value || [])
    .filter(attribute => attribute.qualificationCriterion)
    .map(attribute => {
      const value = customAttributes?.[attribute.attributeKey];
      const hasValue = value !== undefined && value !== null && value !== '';
      return {
        key: attribute.attributeKey,
        label: attribute.attributeDisplayName,
        value: hasValue ? String(value) : '',
        met: hasValue,
        isAiFilled: hasValue && isAiFilledValue(attribute.attributeKey, value),
      };
    });
});

const criteria = computed(() => [
  ...builtInCriteria.value,
  ...customCriteria.value,
]);

const metCount = computed(() => criteria.value.filter(c => c.met).length);
const totalCount = computed(() => criteria.value.length);
const isQualified = computed(
  () => totalCount.value > 0 && metCount.value === totalCount.value
);
const hasCustomCriteria = computed(() => customCriteria.value.length > 0);

const showMarkAsCustomer = computed(
  () => isQualified.value && props.selectedContact?.contactType === 'lead'
);

const markAsCustomer = async () => {
  try {
    await store.dispatch('contacts/update', {
      id: props.selectedContact.id,
      contactType: 'customer',
    });
    useAlert(
      t('CONTACTS_LAYOUT.DETAILS.LIFECYCLE.UPDATE_SUCCESS_MESSAGE', {
        stage: t('CONTACTS_LAYOUT.DETAILS.LIFECYCLE.CUSTOMER'),
      })
    );
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.LIFECYCLE.UPDATE_ERROR_MESSAGE'));
  }
};
</script>

<template>
  <CardLayout>
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div class="flex items-center gap-2">
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.DETAILS.QUALIFICATION.TITLE') }}
        </h4>
        <span
          v-if="isQualified"
          class="inline-flex items-center gap-1.5 px-2 h-6 rounded-full text-xs font-medium bg-n-teal-3 text-n-teal-11"
        >
          <span class="rounded-full size-1.5 bg-current" />
          {{ t('CONTACTS_LAYOUT.DETAILS.QUALIFICATION.QUALIFIED_BADGE') }}
        </span>
      </div>
      <Button
        v-if="showMarkAsCustomer"
        :label="t('CONTACTS_LAYOUT.DETAILS.QUALIFICATION.MARK_CUSTOMER')"
        size="sm"
        color="teal"
        :is-loading="isUpdating"
        :disabled="isUpdating"
        @click="markAsCustomer"
      />
    </div>

    <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
      <div
        v-for="criterion in criteria"
        :key="criterion.key"
        class="flex items-center min-w-0 gap-2.5 py-1.5"
      >
        <span
          class="grid rounded-full shrink-0 size-4 place-items-center text-[10px] font-bold"
          :class="
            criterion.met
              ? 'bg-n-teal-3 text-n-teal-11'
              : 'bg-n-alpha-2 text-n-slate-10'
          "
        >
          {{ criterion.met ? '✓' : '–' }}
        </span>
        <span class="text-sm shrink-0 text-n-slate-12">
          {{ criterion.label }}
        </span>
        <span
          v-if="criterion.met && criterion.value"
          class="min-w-0 text-sm truncate text-n-slate-11 ltr:ml-auto rtl:mr-auto"
        >
          {{ criterion.value }}
        </span>
        <span
          v-if="criterion.isAiFilled"
          class="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wide bg-n-iris-3 text-n-iris-11"
        >
          {{ t('CONTACTS_LAYOUT.DETAILS.QUALIFICATION.AI_SOURCE') }}
        </span>
      </div>
    </div>

    <div class="flex items-center gap-3">
      <div class="flex flex-1 gap-1">
        <div
          v-for="criterion in criteria"
          :key="`segment-${criterion.key}`"
          class="flex-1 h-1.5 rounded-full transition-colors duration-300"
          :class="criterion.met ? 'bg-n-iris-9' : 'bg-n-alpha-2'"
        />
      </div>
      <span
        class="text-sm font-medium text-n-slate-11 tabular-nums whitespace-nowrap"
      >
        {{
          t('CONTACTS_LAYOUT.DETAILS.QUALIFICATION.PROGRESS', {
            met: metCount,
            total: totalCount,
          })
        }}
      </span>
    </div>

    <p v-if="!hasCustomCriteria" class="text-xs text-n-slate-10">
      {{ t('CONTACTS_LAYOUT.DETAILS.QUALIFICATION.NO_CRITERIA_HINT') }}
    </p>
  </CardLayout>
</template>
